import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { apiClient } from '../api/client';
import {
  Loader2, CheckCircle2, Heart, Eye, Footprints, Users, UserPlus, BookOpen,
  Gift, Sparkles, GraduationCap, MessageCircle, PenLine, Home, Palette,
  WifiOff, Smartphone, Smile, HeartHandshake, Megaphone, Wrench, HelpingHand,
  Flag, Trophy, ArrowRight, Gem,
} from 'lucide-react';

const THEME_ICONS = {
  self_care: [Heart, 'text-rose-500', 'bg-rose-100'],
  observation: [Eye, 'text-sky-500', 'bg-sky-100'],
  physical_presence: [Footprints, 'text-emerald-500', 'bg-emerald-100'],
  physical_meetup: [Users, 'text-orange-500', 'bg-orange-100'],
  inviting_connection: [UserPlus, 'text-fuchsia-500', 'bg-fuchsia-100'],
  reading_stillness: [BookOpen, 'text-indigo-500', 'bg-indigo-100'],
  secret_kindness: [Gift, 'text-pink-500', 'bg-pink-100'],
  self_reflection: [Sparkles, 'text-violet-500', 'bg-violet-100'],
  skill_sharing: [GraduationCap, 'text-blue-500', 'bg-blue-100'],
  stranger_interaction: [MessageCircle, 'text-teal-500', 'bg-teal-100'],
  written_communication: [PenLine, 'text-cyan-600', 'bg-cyan-100'],
  family_connection: [Home, 'text-amber-500', 'bg-amber-100'],
  creative_making: [Palette, 'text-purple-500', 'bg-purple-100'],
  digital_detox_extended: [WifiOff, 'text-slate-500', 'bg-slate-100'],
  digital_detox_l1: [Smartphone, 'text-lime-600', 'bg-lime-100'],
  gratitude_expression: [Smile, 'text-yellow-500', 'bg-yellow-100'],
  community_kindness: [HeartHandshake, 'text-red-500', 'bg-red-100'],
  public_contribution: [Megaphone, 'text-orange-600', 'bg-orange-100'],
  home_contribution: [Wrench, 'text-stone-500', 'bg-stone-100'],
  community_service: [HelpingHand, 'text-green-600', 'bg-green-100'],
};

const ChallengesPage = () => {
  const navigate = useNavigate();
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.request('/api/schools/journey/list')
      .then(setJourney)
      .catch(() => setJourney({ has_journey: false, challenges: [] }))
      .finally(() => setLoading(false));
  }, []);

  const goToNeo = async () => {
    try {
      const avas = await apiClient.getAvas();
      const neo = (avas || []).find((a) => (a.name || '').trim().toLowerCase() === 'neo');
      if (neo) {
        navigate('/conversations/chat', { state: { selectedAva: neo } });
      } else {
        navigate('/conversations');
      }
    } catch {
      navigate('/conversations');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-24" data-testid="challenges-loading">
          <Loader2 className="animate-spin text-purple-500" size={32} />
        </div>
      </DashboardLayout>
    );
  }

  if (!journey?.has_journey) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto text-center py-16 px-4" data-testid="challenges-no-journey">
          <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-purple-100 flex items-center justify-center">
            <Flag className="text-purple-600" size={28} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Your Challenge Journey is waiting</h1>
          <p className="text-gray-600 mb-6">
            Complete your registration questions and we'll create your very own
            20-challenge journey — built around who you are. Small, doable steps.
            Big real-world confidence.
          </p>
          <button
            onClick={() => navigate('/profile')}
            className="px-6 py-3 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 transition-colors"
            data-testid="challenges-complete-quiz-btn"
          >
            Complete my questions
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const { challenges, completed_count, total_count } = journey;
  const pct = total_count ? Math.round((completed_count / total_count) * 100) : 0;
  const current = challenges.find((c) => c.is_current);

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto px-4 pb-16" data-testid="challenges-journey-page">
        {/* Header + progress */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">Your Challenge Journey</h1>
          <p className="text-gray-600 mb-4">
            {total_count} small, doable challenges — done in order, one at a time. You've absolutely got this.
          </p>
          <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <p className="font-semibold text-gray-900" data-testid="journey-progress-label">
                {completed_count} of {total_count} completed
              </p>
              <p className="text-sm text-purple-600 font-semibold">{pct}%</p>
            </div>
            <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all"
                style={{ width: `${Math.max(pct, 3)}%` }}
                data-testid="journey-progress-bar"
              />
            </div>
            {current && (
              <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold text-purple-700">Up now:</span>{' '}
                  Challenge No {current.sequence} — {current.theme_label}
                </p>
                <button
                  onClick={goToNeo}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-semibold rounded-xl hover:bg-purple-700 transition-colors"
                  data-testid="journey-practice-with-neo-btn"
                >
                  Practice with Neo <ArrowRight size={15} />
                </button>
              </div>
            )}
            {!current && (
              <p className="mt-4 text-sm font-semibold text-green-700 flex items-center gap-2">
                <Trophy size={16} /> You've completed every challenge — incredible!
              </p>
            )}
          </div>
        </div>

        {/* The 20 numbered challenges — split at the NFT rewards zone */}
        {(() => {
          const renderCard = (c) => {
            const [Icon, iconColor, iconBg] = THEME_ICONS[c.theme_key] || [Sparkles, 'text-violet-500', 'bg-violet-100'];
            const done = c.status === 'completed';
            const nftZone = c.sequence > 10;
            const cardStyle = done
              ? 'bg-green-50 border-green-300 shadow-md'
              : c.is_current
                ? 'bg-white border-purple-500 ring-2 ring-purple-200 shadow-lg'
                : nftZone
                  ? 'bg-white border-amber-200 shadow-md hover:shadow-lg hover:-translate-y-0.5'
                  : 'bg-white border-gray-300 shadow-md hover:shadow-lg hover:-translate-y-0.5';
            return (
              <div
                key={c.sequence}
                className={`relative rounded-2xl border-2 p-4 transition-all duration-200 ${cardStyle}`}
                data-testid={`journey-challenge-card-${c.sequence}`}
              >
                {nftZone && (
                  <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow" title="Completing this challenge mints an NFT into your Vault">
                    <Gem size={12} className="text-white" />
                  </span>
                )}
                <div className="flex items-center justify-between mb-3">
                  <span
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shadow-sm ${
                      done ? 'bg-green-500 text-white' : c.is_current ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {c.sequence}
                  </span>
                  {done ? (
                    <CheckCircle2 className="text-green-500" size={22} data-testid={`journey-challenge-done-${c.sequence}`} />
                  ) : (
                    <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${iconBg}`}>
                      <Icon className={iconColor} size={18} />
                    </span>
                  )}
                </div>
                <p className={`font-semibold text-sm ${done ? 'text-green-800' : 'text-gray-900'}`}>
                  {c.theme_label}
                </p>
                {c.is_current && (
                  <p className="mt-1 text-xs font-semibold text-purple-600" data-testid="journey-you-are-here">
                    You are here ✦
                  </p>
                )}
                {done && c.completed_at && (
                  <p className="mt-1 text-xs text-green-600">Done {String(c.completed_at).slice(0, 10)}</p>
                )}
              </div>
            );
          };
          const firstTen = challenges.filter((c) => c.sequence <= 10);
          const nftTen = challenges.filter((c) => c.sequence > 10);
          return (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="journey-challenge-grid">
                {firstTen.map(renderCard)}
              </div>

              {/* NFT rewards divider */}
              {nftTen.length > 0 && (
                <div
                  className="my-8 rounded-2xl bg-gradient-to-r from-amber-400 via-orange-400 to-pink-500 p-[2px] shadow-lg"
                  data-testid="journey-nft-divider"
                >
                  <div className="rounded-2xl bg-white/95 px-5 py-4 flex flex-col sm:flex-row items-center gap-3 text-center sm:text-left">
                    <span className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow flex-shrink-0">
                      <Gem size={22} className="text-white" />
                    </span>
                    <div>
                      <p className="font-bold text-gray-900">The NFT Rewards Zone starts here 💎</p>
                      <p className="text-sm text-gray-600">
                        Challenge No 10 is your Graduation — from No 11 onwards, every challenge you
                        complete mints a real NFT straight into your Vault.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="journey-nft-grid">
                {nftTen.map(renderCard)}
              </div>
            </>
          );
        })()}

        <p className="mt-8 text-center text-sm text-gray-500">
          Every challenge is small on purpose. One at a time, in order — and before you know it, you're at the finish line. 🏁
        </p>
      </div>
    </DashboardLayout>
  );
};

export default ChallengesPage;
